import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { Download, QrCode } from "lucide-react";

export const HOUSING_QR_OPTIONS = Object.freeze({
  errorCorrectionLevel: "H",
  margin: 4,
  width: 1200,
  color: {
    dark: "#001f4d",
    light: "#ffffff",
  },
});

export function createHousingQrPngDataUrl(url) {
  return QRCode.toDataURL(url, {
    ...HOUSING_QR_OPTIONS,
    type: "image/png",
  });
}

export function createHousingQrSvg(url) {
  return QRCode.toString(url, {
    ...HOUSING_QR_OPTIONS,
    type: "svg",
  });
}

function safeDownloadName(value, extension) {
  const base = String(value || "housing-development-survey-qr")
    .trim()
    .replace(/\.(png|svg)$/i, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "") || "housing-development-survey-qr";
  return `${base}.${extension}`;
}

function downloadHref(href, filename) {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function HousingDevelopmentQr({
  url,
  filename = "housing-development-survey-qr",
  title = "Survey QR code",
}) {
  const [pngDataUrl, setPngDataUrl] = useState("");
  const [svgMarkup, setSvgMarkup] = useState("");
  const [error, setError] = useState("");
  const safeUrl = useMemo(() => String(url || "").trim(), [url]);

  useEffect(() => {
    let active = true;
    setPngDataUrl("");
    setSvgMarkup("");
    setError("");

    if (!safeUrl) return () => {
      active = false;
    };

    Promise.all([
      createHousingQrPngDataUrl(safeUrl),
      createHousingQrSvg(safeUrl),
    ])
      .then(([png, svg]) => {
        if (!active) return;
        setPngDataUrl(png);
        setSvgMarkup(svg);
      })
      .catch(() => {
        if (!active) return;
        setError("The QR code could not be generated. Please try again.");
      });

    return () => {
      active = false;
    };
  }, [safeUrl]);

  function downloadPng() {
    if (pngDataUrl) downloadHref(pngDataUrl, safeDownloadName(filename, "png"));
  }

  function downloadSvg() {
    if (!svgMarkup) return;
    const objectUrl = URL.createObjectURL(
      new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" })
    );
    downloadHref(objectUrl, safeDownloadName(filename, "svg"));
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  if (!safeUrl) {
    return (
      <p role="status" className="text-sm text-amber-200">
        Add a survey URL to generate its QR code.
      </p>
    );
  }

  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-4" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold text-white">
            <QrCode className="h-5 w-5" aria-hidden="true" />
            {title}
          </h3>
          <p className="mt-1 break-all text-xs text-white/65">{safeUrl}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={downloadPng}
            disabled={!pngDataUrl}
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download PNG
          </button>
          <button
            type="button"
            onClick={downloadSvg}
            disabled={!svgMarkup}
            className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download SVG
          </button>
        </div>
      </div>
      {error ? (
        <p role="alert" className="mt-4 text-sm text-red-200">{error}</p>
      ) : pngDataUrl ? (
        <img
          src={pngDataUrl}
          alt={`${title}. Scan to open ${safeUrl}`}
          className="mx-auto mt-4 w-full max-w-xs rounded-xl bg-white p-3"
        />
      ) : (
        <p role="status" className="mt-4 text-sm text-white/70">Generating QR code…</p>
      )}
    </section>
  );
}
